import { FormControlLabel, Grid, Switch } from '@mui/material';
import { useFormik } from 'formik';
import { Fragment, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { BackButton } from 'src/components/shared/Button/BackButton';
import {
  PrimaryButton,
  SecondaryButton,
  TertiaryButton
} from 'src/components/shared/Button/Button';
import { Checkbox } from 'src/components/shared/Checkbox/Checkbox';
import { DescriptionList } from 'src/components/shared/DescriptionList/DescriptionList';
import { Divider } from 'src/components/shared/Divider/Divider';
import { AuditLog } from 'src/components/shared/Forms/AuditLog/AuditLog';
import { Autocomplete } from 'src/components/shared/Forms/Autocomplete/Autocomplete';
import { FormField } from 'src/components/shared/Forms/FormHelpers';
import { InputField } from 'src/components/shared/Forms/Input/InputField';
import { StickyFormHeader } from 'src/components/shared/Forms/StickyFormHeader/StickyFormHeader';
import { Icon } from 'src/components/shared/Icon/Icon';
import { Header4 } from 'src/components/shared/Typography/Header4';
import { RootState } from 'src/store';
import { CustomerDto } from 'src/store/customers/customer';
import { selectCustomers } from 'src/store/customers/customers.selectors';
import { GradeDto } from 'src/store/grades/grade';
import { selectActiveGrades } from 'src/store/grades/grades.selectors';
import { LabelTemplateDto } from 'src/store/labelTemplates/labelTemplate';
import { selectLabelTemplates } from 'src/store/labelTemplates/labelTemplates.selectors';
import { selectMixtureTypes } from 'src/store/mixturetypes/mixturetypes.selectors';
import { PackagingDto } from 'src/store/packagings/packaging';
import { selectPackagings } from 'src/store/packagings/packagings.selectors';
import { PalletDto } from 'src/store/pallets/pallet';
import { selectPallets } from 'src/store/pallets/pallets.selectors';
import { PlantDto } from 'src/store/plants/plant';
import { selectPlants } from 'src/store/plants/plants.selectors';
import { ProductTypeDto } from 'src/store/producttypes/producttype';
import { selectActiveProductTypes } from 'src/store/producttypes/producttypes.selectors';
import { SegmentDto } from 'src/store/segments/segment';
import { selectSegmentsByProductType } from 'src/store/segments/segments.selectors';
import { SpecificationDetailDto } from 'src/store/specifications/specification';
import { UserRole } from 'src/store/users/user';
import {
  maxLength,
  required,
  runValidations,
  validationRunner
} from 'src/utils/validation';
import { isUserAdmin, useAuth } from '../../Auth/AuthContext';
import { Authorization } from '../../Auth/Authorization';

interface Props {
  specification: Nullable<SpecificationDetailDto>;
  onCreateVismaProduct: () => void;
  onCancel: () => void;
  onSubmit: (spec: SpecificationDetailDto) => void;
  onDownloadSpecificationData: () => void;
}

export const SpecificationDetailsForm = (props: Props) => {
  const { t } = useTranslation();

  const { user } = useAuth();

  const [specification, setSpecification] = useState<SpecificationDetailDto>({
    active: true,
    lockWaybill: false
  } as SpecificationDetailDto);

  const [plant, setPlant] = useState<Nullable<PlantDto>>(null);
  const [productType, setProductType] =
    useState<Nullable<ProductTypeDto>>(null);
  const [segment, setSegment] = useState<Nullable<SegmentDto>>(null);
  const [grade, setGrade] = useState<Nullable<GradeDto>>(null);
  const [customer, setCustomer] = useState<Nullable<CustomerDto>>(null);
  const [packaging, setPackaging] = useState<Nullable<PackagingDto>>(null);
  const [pallet, setPallet] = useState<Nullable<PalletDto>>(null);
  const [labelTemplate, setLabelTemplate] =
    useState<Nullable<LabelTemplateDto>>(null);

  const [showErrors, setShowErrors] = useState<boolean>(false);
  const [showAuditLog, setShowAuditLog] = useState<boolean>(false);

  const plants$ = useSelector(selectPlants);
  const productTypes$ = useSelector(selectActiveProductTypes);

  const segments$ = useSelector((state: RootState) =>
    selectSegmentsByProductType(state.segments, productType?.id)
  );

  const grades$ = useSelector(selectActiveGrades);
  const customers$ = useSelector(selectCustomers);
  const packagings$ = useSelector(selectPackagings);
  const pallets$ = useSelector(selectPallets);
  const labelTemplates$ = useSelector(selectLabelTemplates);
  const mixtureTypes$ = useSelector(selectMixtureTypes);

  useEffect(() => {
    if (props.specification) {
      setSpecification(props.specification);
      setPlant(props.specification.plant);
      setProductType(props.specification.productType);
      setSegment(props.specification.segment);
      setGrade(props.specification.grade);
      setCustomer(props.specification.customer);
      setPackaging(props.specification.packaging);
      setPallet(props.specification.pallet);
      setLabelTemplate(props.specification.labelTemplate);
    }
  }, [props.specification]);

  const validate = (specification: SpecificationDetailDto) => {
    const validationRunners = [
      validationRunner('no', t('props.number'), required),
      validationRunner('name', t('props.name'), required, maxLength(200)),
      validationRunner('reference', t('props.reference'), maxLength(200)),
      validationRunner(
        'specialMarking',
        t('props.specialMarking'),
        maxLength(500)
      ),
      // validationRunner(
      //   'labelTemplate',
      //   t('props.labelTemplate'),
      //   maxLength(200)
      // ),
      validationRunner('description', t('props.description'), maxLength(500)),
      validationRunner('plantId', t('plant.singular'), required),
      validationRunner('productTypeId', t('productType.singular'), required),
      validationRunner('segmentId', t('segment.singular'), required),
      validationRunner('gradeId', t('grade.singular'), required),
      validationRunner('customerId', t('customer.singular'), required),
      validationRunner('packagingId', t('packaging.singular'), required),
      validationRunner('palletId', t('pallet.singular'), required)
      // validationRunner('labelTemplateId', t('labelTemplate.singular'), required)
    ];
    return runValidations(specification, validationRunners);
  };

  const formik = useFormik({
    initialValues: specification,
    enableReinitialize: true,
    validate: validate,
    onSubmit: (values, { setSubmitting }) => {
      props.onSubmit(values);
      setSubmitting(false);
    }
  });

  const {
    values,
    errors,
    touched,
    handleChange,
    handleBlur,
    handleSubmit,
    setFieldValue,
    isSubmitting,
    submitCount
  } = formik;

  const LabelListItem = (
    props: React.HTMLAttributes<HTMLLIElement>,
    labelTemplate: LabelTemplateDto
  ) => (
    <li {...props}>
      <Grid container>
        <Grid item xs={2}>
          {labelTemplate.no}
        </Grid>
        <Grid item xs={10}>
          {labelTemplate.name}
        </Grid>
      </Grid>
    </li>
  );

  useEffect(() => {
    const getNo = (
      val: string | number | undefined,
      length: number
    ): string => {
      return ('0000' + (val ?? 0).toString()).slice(-1 * length);
    };

    const no = `${getNo(plant?.no, 1)}-${getNo(productType?.no, 1)}-${getNo(
      segment?.no,
      2
    )}-${getNo(grade?.no, 3)}-${getNo(customer?.no, 4)}-${getNo(
      packaging?.no,
      2
    )}-${getNo(pallet?.no, 2)}`;

    setFieldValue('no', no);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [plant, productType, segment, grade, customer, packaging, pallet]);

  return (
    <Fragment>
      <StickyFormHeader className="flex align-center jc-sb">
        <DescriptionList>
          <BackButton onClick={props.onCancel} />
          <li>
            <Header4>{t('props.number')}</Header4>
            {values.no || '-'}
          </li>
          <li>
            <Header4>{t('props.lastUpdated')}</Header4>
            {values.user
              ? `${values.user.firstName} ${values.user.lastName}`
              : '-'}
          </li>
          <li>
            <Header4>{t('props.audit')}</Header4>
            {values.revision}
            <Icon
              className="ml-32 pointer"
              size={20}
              type="la-edit"
              onClick={() => setShowAuditLog(!showAuditLog)}
            />
          </li>
        </DescriptionList>
        <div className="flex jc-end">
          <FormControlLabel
            control={
              <Switch
                checked={values.active}
                onChange={handleChange}
                disabled={!isUserAdmin(user)}
                name="active"
              />
            }
            label={t('props.active')}
          />
          <TertiaryButton
            onClick={props.onCancel}
            label={t('cancel')}
            disabled={!isUserAdmin(user)}
            className="mr-32"
          />
          <PrimaryButton
            type="submit"
            label={t('save.verb')}
            disabled={isSubmitting || !isUserAdmin(user)}
            onClick={() => {
              setShowErrors(true);
              handleSubmit();
            }}
          />
        </div>
      </StickyFormHeader>
      <form onSubmit={handleSubmit}>
        <Grid container>
          <Grid item xs={12} md={8}>
            <InputField
              name="name"
              label={t('props.name')}
              value={values.name}
              onChange={handleChange}
              disabled={!isUserAdmin(user)}
              onBlur={handleBlur}
              errorMessage={errors.name}
              displayError={touched.name || submitCount > 0}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <Authorization role={UserRole.Admin}>
              <SecondaryButton
                className="mr-32"
                label={t('specification.createVismaProduct')}
                disabled={!isUserAdmin(user)}
                onClick={() => {
                  props.onCreateVismaProduct();
                }}
              ></SecondaryButton>
            </Authorization>
            <SecondaryButton
              icon="la-file-excel"
              label={t('specification.specificationdata')}
              disabled={!isUserAdmin(user)}
              onClick={() => {
                props.onDownloadSpecificationData();
              }}
            ></SecondaryButton>
          </Grid>
        </Grid>

        <Divider />

        <Grid container>
          <Grid item xs={12} md={4}>
            <FormField>
              <Autocomplete
                options={plants$}
                label={t('plant.singular')}
                optionField="name"
                value={plant}
                disabled={!isUserAdmin(user)}
                onChange={plant => {
                  setFieldValue('plantId', plant?.id);
                  setPlant(plant);
                }}
                onBlur={handleBlur}
                errorMessage={errors.plantId}
                displayError={!!errors.plantId && showErrors}
              />
            </FormField>
          </Grid>
          <Grid item xs={12} md={4}>
            <FormField>
              <Autocomplete
                options={productTypes$}
                label={t('productType.singular')}
                optionField="name"
                value={productType}
                disabled={!isUserAdmin(user)}
                onChange={productType => {
                  setSegment(null);
                  setTimeout(() => {
                    setFieldValue('productTypeId', productType?.id);
                    setProductType(productType);
                  });
                }}
                onBlur={handleBlur}
                errorMessage={errors.productTypeId}
                displayError={!!errors.productTypeId && showErrors}
              />
            </FormField>
          </Grid>
          <Grid item xs={12} md={4}>
            <FormField>
              <Autocomplete
                options={segments$}
                label={t('segment.singular')}
                optionField="name"
                value={segment}
                disabled={!isUserAdmin(user)}
                onChange={segment => {
                  setFieldValue('segmentId', segment?.id);
                  setSegment(segment);
                }}
                onBlur={handleBlur}
                errorMessage={errors.segmentId}
                displayError={!!errors.segmentId && showErrors}
              />
            </FormField>
          </Grid>
        </Grid>
        <Grid container>
          <Grid item xs={12} md={4}>
            <FormField>
              <Autocomplete
                options={grades$}
                label={t('grade.singular')}
                optionField="name"
                value={grade}
                disabled={!isUserAdmin(user)}
                onChange={grade => {
                  setFieldValue('gradeId', grade?.id);
                  setGrade(grade);
                }}
                onBlur={handleBlur}
                errorMessage={errors.gradeId}
                displayError={!!errors.gradeId && showErrors}
              />
            </FormField>
          </Grid>
          <Grid item xs={12} md={4}>
            <FormField>
              <Autocomplete
                options={customers$}
                label={t('customer.singular')}
                optionField="name"
                value={customer}
                disabled={!isUserAdmin(user)}
                onChange={customer => {
                  setFieldValue('customerId', customer?.id);
                  setCustomer(customer);
                }}
                onBlur={handleBlur}
                errorMessage={errors.customerId}
                displayError={!!errors.customerId && showErrors}
              />
            </FormField>
          </Grid>
          <Grid item xs={12} md={4}>
            <FormField>
              <Autocomplete
                options={packagings$}
                label={t('packaging.singular')}
                optionField="name"
                value={packaging}
                disabled={!isUserAdmin(user)}
                onChange={packaging => {
                  setFieldValue('packagingId', packaging?.id);
                  setPackaging(packaging);
                }}
                onBlur={handleBlur}
                errorMessage={errors.packagingId}
                displayError={!!errors.packagingId && showErrors}
              />
            </FormField>
          </Grid>
        </Grid>
        <Grid container>
          <Grid item xs={12} md={4}>
            <FormField>
              <Autocomplete
                options={pallets$}
                label={t('pallet.singular')}
                optionField="name"
                value={pallet}
                disabled={!isUserAdmin(user)}
                onChange={pallet => {
                  setFieldValue('palletId', pallet?.id);
                  setPallet(pallet);
                }}
                onBlur={handleBlur}
                errorMessage={errors.palletId}
                displayError={!!errors.palletId && showErrors}
              />
            </FormField>
          </Grid>
          <Grid item xs={12} md={4}>
            <FormField>
              <Autocomplete
                options={mixtureTypes$}
                label={t('mixtureType.singular')}
                optionField="name"
                value={values.mixtureType}
                disabled={!isUserAdmin(user)}
                onChange={mixtureType =>
                  setFieldValue('mixtureTypeId', mixtureType?.id)
                }
                onBlur={handleBlur}
                errorMessage={errors.mixtureTypeId}
                displayError={!!errors.mixtureTypeId && showErrors}
              />
            </FormField>
          </Grid>
          <Grid item xs={12} md={4}>
            <Grid container>
              <Grid item xs={12} md={3}>
                <InputField
                  name="labelTemplateText"
                  label={t('props.labelTemplate')}
                  value={values.labelTemplateText}
                  disabled={!isUserAdmin(user)}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  errorMessage={errors.labelTemplateText}
                  displayError={touched.labelTemplateText || submitCount > 0}
                />
              </Grid>
              <Grid item xs={12} md={9}>
                <FormField>
                  <Autocomplete
                    options={labelTemplates$}
                    label={t('labelTemplate.singular')}
                    optionField="name"
                    disabled={!isUserAdmin(user)}
                    onChange={labelTemplate => {
                      setFieldValue('labelTemplateId', labelTemplate?.id);
                      setLabelTemplate(labelTemplate);
                    }}
                    onBlur={handleBlur}
                    errorMessage={errors.labelTemplateId}
                    displayError={!!errors.labelTemplateId && showErrors}
                    value={labelTemplate}
                    getOptionLabel={option => `${option.no} - ${option.name}`}
                    renderOption={(props, option) =>
                      LabelListItem(props, option)
                    }
                    filterOptions={(options, state) =>
                      options.filter(o =>
                        `${o.no} ${o.name}`
                          .toLocaleLowerCase()
                          .includes(state.inputValue.toLocaleLowerCase())
                      )
                    }
                  />
                </FormField>
              </Grid>
            </Grid>
          </Grid>
        </Grid>
        <Grid>
          <Grid item xs={12} md={12}>
            <Checkbox
              label={t('props.lockWaybill')}
              checked={values.lockWaybill}
              disabled={!isUserAdmin(user)}
              name="lockWaybill"
              onChange={handleChange}
            />
          </Grid>
        </Grid>

        <Divider />

        <Grid container>
          <Grid item xs={12} md={12}>
            <InputField
              name="reference"
              label={t('props.reference')}
              value={values.reference}
              disabled={!isUserAdmin(user)}
              onChange={handleChange}
              onBlur={handleBlur}
              errorMessage={errors.reference}
              displayError={touched.reference || submitCount > 0}
            />
            <InputField
              name="specialMarking"
              label={t('props.specialMarking')}
              multiline
              rows={4}
              value={values.specialMarking}
              disabled={!isUserAdmin(user)}
              onChange={handleChange}
              onBlur={handleBlur}
              errorMessage={errors.specialMarking}
              displayError={touched.specialMarking || submitCount > 0}
            />
            <InputField
              name="description"
              label={t('props.description')}
              multiline
              rows={4}
              value={values.description}
              disabled={!isUserAdmin(user)}
              onChange={handleChange}
              onBlur={handleBlur}
              errorMessage={errors.description}
              displayError={touched.description || submitCount > 0}
            />
          </Grid>
        </Grid>
      </form>
      <AuditLog
        open={showAuditLog}
        onChange={audit => {
          setFieldValue('revision', audit.revisionNo);
          setFieldValue('revisionDate', audit.revisionDate);
          setFieldValue('internalRevision', audit.internalRevisionNo);
          setFieldValue('internalRevisionDate', audit.internalRevisionDate);
        }}
        onCancel={() => setShowAuditLog(false)}
        revisionNo={values.revision}
        revisionDate={values.revisionDate}
        internalRevisionNo={values.internalRevision}
        internalRevisionDate={values.internalRevisionDate}
      />
    </Fragment>
  );
};
